import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../../../redux-store";
import {
  selectInvoiceLines,
  selectInvoiceLinesForCreate,
} from "../../../../../ducks/data/deliveries/selectors";
import {
  addLine as addLineThunk,
  updateLine as updateLineThunk,
  deleteLine as deleteLineThunk,
  updateLineOrderNumber,
  updateSurchargeLine as updateSurchargeLineThunk,
} from "../../../../../ducks/data/deliveries";
import {
  computeDraftLines,
  draftLineReordered,
} from "../../../../../ducks/data/draft-invoice-lines";
import {
  CreateUpdateInvoiceLine,
  Line,
} from "../../../../../ducks/data/deliveries/types";

export interface InvoiceLinesController {
  lines: Line[];
  addLine: (line: CreateUpdateInvoiceLine) => Promise<string[]>;
  updateLine: (id: string, line: CreateUpdateInvoiceLine) => Promise<unknown>;
  updateSurchargeLine: (id: string, percentage: string) => Promise<unknown>;
  deleteLine: (lineId: string) => Promise<unknown>;
  reorderLine: (lineId: string, toIndex: number) => Promise<unknown>;
}

/**
 * Single point that decides where invoice lines live. In normal (edit) mode the
 * lines belong to a persisted delivery and every mutation goes through the
 * server thunks. In create mode there is no delivery yet, so the lines are held
 * in memory and recomputed through the stateless compute endpoint, keeping the
 * surcharge behaviour identical to the server. Reordering has no surcharge
 * effect, so in create mode it is a purely local reducer.
 */
export const useInvoiceLines = ({
  deliveryId,
  clientId,
  createNew,
}: {
  deliveryId: string;
  clientId?: string;
  createNew?: boolean;
}): InvoiceLinesController => {
  const dispatch = useAppDispatch();
  const lines = useSelector(
    createNew ? selectInvoiceLinesForCreate() : selectInvoiceLines(deliveryId)
  );

  const addLine = useCallback(
    (line: CreateUpdateInvoiceLine) => {
      if (createNew) {
        return dispatch(
          computeDraftLines({
            clientId: clientId ?? null,
            op: { type: "ADD", line },
          })
        ).unwrap();
      }
      return dispatch(addLineThunk({ deliveryId, ...line })).unwrap();
    },
    [dispatch, createNew, clientId, deliveryId]
  );

  const updateLine = useCallback(
    (id: string, line: CreateUpdateInvoiceLine) => {
      if (createNew) {
        return dispatch(
          computeDraftLines({
            clientId: clientId ?? null,
            op: { type: "UPDATE", id, line },
          })
        ).unwrap();
      }
      return dispatch(updateLineThunk({ deliveryId, id, ...line }));
    },
    [dispatch, createNew, clientId, deliveryId]
  );

  const updateSurchargeLine = useCallback(
    (id: string, percentage: string) => {
      if (createNew) {
        return dispatch(
          computeDraftLines({
            clientId: clientId ?? null,
            op: { type: "UPDATE_SURCHARGE", id, percentage },
          })
        ).unwrap();
      }
      return dispatch(updateSurchargeLineThunk({ deliveryId, id, percentage }));
    },
    [dispatch, createNew, clientId, deliveryId]
  );

  const deleteLine = useCallback(
    (lineId: string) => {
      if (createNew) {
        return dispatch(
          computeDraftLines({
            clientId: clientId ?? null,
            op: { type: "DELETE", lineId },
          })
        ).unwrap();
      }
      return dispatch(deleteLineThunk({ deliveryId, lineId })).unwrap();
    },
    [dispatch, createNew, clientId, deliveryId]
  );

  const reorderLine = useCallback(
    (lineId: string, toIndex: number) => {
      if (createNew) {
        dispatch(draftLineReordered({ lineId, toIndex }));
        return Promise.resolve();
      }
      return dispatch(
        updateLineOrderNumber({ deliveryId, lineId, orderNumber: toIndex })
      );
    },
    [dispatch, createNew, deliveryId]
  );

  return {
    lines,
    addLine,
    updateLine,
    updateSurchargeLine,
    deleteLine,
    reorderLine,
  };
};
